import { Injectable } from "@angular/core";
import { BaseFacade } from "@webapp/core/abstracts/services/base-facade.service";
import { PermissionsApiService } from "./permissions-api.service";
import { BaseFilterModel } from "@webapp/core/abstracts/models/base-filter.model";
import { Permission, PermissionDTO } from "@webapp/permissions/models/permissions.model";
import { PermissionsState } from "@webapp/permissions/services/pemissions-state.service";
import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import { HttpActions } from "@webapp/core/abstracts/enums/http-actions.enum";

// we provide this in root so we fetch the permissions only 1 time
@Injectable({
  providedIn: "root",
})
export class PermissionsFacade extends BaseFacade<Permission, PermissionDTO, PermissionsState, PermissionsApiService> {
  private loadedPermissionsStream$: BehaviorSubject<Permission[]> = new BehaviorSubject([]);

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(state: PermissionsState, api: PermissionsApiService) {
    super(state, api);
    // we need this here for direct calls from Angular1 -> Angular 12 component (not passing through route resolve)
    this.loadEffectivePermissions().then((permissions) => {
      this.setPermissions(permissions);
    });
  }
  // we need this to be an async / await function
  loadEffectivePermissions(filters?: BaseFilterModel, reloadPermissions = false): Promise<Permission[]> {
    // checking the current value of the permissions stream of for a reload event
    if (!this.loadedPermissionsStream$.getValue().length || reloadPermissions) {
      // we set the loading to true
      this.resetInitialState(HttpActions.get);
      // returning the updated permissions
      const result = lastValueFrom(this.apiService.getEffectivePermissions$(filters));
      // we handle request finalization
      this.handleRequestFinalized(HttpActions.getAll);

      return result;
    }
    // returning the currentPermissionStream
    return lastValueFrom(this.loadedPermissionsStream$);
  }

  // synchronous call of the checkPermissions - this can be used directly in A12 or after a getPermissions$ check in Angular1-called components
  hasPermission(action: Permission): boolean {
    return this.loadedPermissionsStream$.getValue().length > 0 && this.loadedPermissionsStream$.getValue().indexOf(action) > -1;
  }

  // this needs to be observed for changes so we know when the permissions are loaded - used when the component is called from Angular1 route
  getPermissions$(): Observable<Permission[]> {
    return this.loadedPermissionsStream$.asObservable();
  }
  // this is used to set the permissions to the observable
  setPermissions(permissions: Permission[]): void {
    this.loadedPermissionsStream$.next(permissions);
  }
}
